import { MethodOfWork, PermitCategory, SignatureType } from "../../../common/enums";
import { String_extractIndex, String_deCapitalize } from "../../../common/stringUtils";
import FormElement, { formElement } from "./../FormElement";
import ProblemDetails from "../../responses/ProblemDetails";
import Signature, { Signature_empty } from "../../Signature";
import GeneralDepartmentPermit from "../../permits/GeneralDepartmentPermit";
import { Array_lastOrDefault } from "../../../common/arrayUtils";
import { ParticipantListItem } from "../../../components/Forms/Permits/ParticipantsList";
import PositionPickerRow, { PositionPickerRow_default } from "./PositionPickerRow";
import { Date_toDateString } from "../../../common/dateUtils";
import ProtectiveEquipmentFormState, {
    ProtectiveEquipmentFormState_default
} from "./Conditions/ProtectiveEquipmentFormState";
import RiskIdentificationsFormState, {
    RiskIdentificationsFormState_default
} from "./Conditions/RiskIdentificationsFormState";
import MeasuresFormState, { MeasuresFormState_default } from "./Conditions/MeasuresFormState";
import { Measures_toFormState } from "../../permits/conditions/Measures";
import { RiskIdentifications_toFormState } from "../../permits/conditions/RiskIdentifications";
import { ProtectiveEquipment_toFormState } from "../../permits/conditions/ProtectiveEquipment";
import { Position_toPositionPickerRow } from "../../Position";

type GeneralDepartmentPermitFormState = {
    positions: PositionPickerRow[];
    startDate: FormElement<Date>;
    endDate: FormElement<Date>;
    remindMe: FormElement<boolean>;
    expirationReminderEmail: FormElement<string>;
    expirationReminderPhoneNumber: FormElement<string>;
    remindMeDate: FormElement<Date>;
    permitCategory: FormElement<PermitCategory>;
    entrepreneur: FormElement<string>;
    description: FormElement<string>;
    workLeaderName: FormElement<string>;
    workLeaderIsActive: FormElement<boolean>;
    workLeaderPhoneNumber: FormElement<string>;
    persons: ParticipantListItem[];
    projectLeaderName: FormElement<string>;
    projectLeaderPhoneNumber: FormElement<string>;
    projectLeaderIsActive: FormElement<boolean>;
    isWorkFlammable: FormElement<boolean>;
    methodOfWork: FormElement<MethodOfWork[]>;
    otherMethodOfWork: FormElement<string>;
    isAuthorizedForHotWork: FormElement<boolean>;
    surveillanceByFireWatch: FormElement<boolean>;
    fireWatchName: FormElement<string>;
    fireWatchNotNeeded: FormElement<boolean>;
    postWorkSurveillanceArranged: FormElement<boolean>;
    postWorkSurveillanceFireWatch: FormElement<string>;
    postWorkSurveillanceHours: FormElement<number>;
    forWorkWithFlammableGoods: FormElement<boolean>;
    workplaceCleaned: FormElement<boolean>;
    combustibleMaterialsRemoved: FormElement<boolean>;
    inAreaWithExplosiveAtmosphere: FormElement<boolean>;
    openings: FormElement<boolean>;
    extinguishingEquipment: FormElement<boolean>;
    weldingEquipment: FormElement<boolean>;
    weldingEquipmentFaultless: FormElement<boolean>;
    emergencyServicesCanBeCalled: FormElement<boolean>;
    waterproofing: FormElement<boolean>;
    openFlamesForMeltingIce: FormElement<boolean>;
    maximumHeat: FormElement<boolean>;
    whenMeltingAsphalt: FormElement<boolean>;
    followingSections: FormElement<string>;
    permitAgreementStartDate: FormElement<Date>;
    permitAgreementEndDate: FormElement<Date>;
    permitAgreementFireWatch: FormElement<string>;
    permitAgreementWayOfCommunication: FormElement<string>;
    permitAgreementProviderSignature: Signature;
    permitAgreementSeekerSignature: Signature;
    isFinishedPermitProviderSignature: Signature;
    isFinishedPermitSeekerSignature: Signature;

    measures: MeasuresFormState;
    protectiveEquipment: ProtectiveEquipmentFormState;
    riskIdentifications: RiskIdentificationsFormState;
};

export default GeneralDepartmentPermitFormState;

export const defaultFormState = (): GeneralDepartmentPermitFormState => ({
    positions: [PositionPickerRow_default()],
    startDate: formElement(new Date()),
    endDate: formElement(new Date()),
    remindMe: formElement(false),
    expirationReminderEmail: formElement(""),
    expirationReminderPhoneNumber: formElement(""),
    remindMeDate: formElement(new Date()),
    permitCategory: formElement(PermitCategory.ColdWork),
    entrepreneur: formElement(""),
    description: formElement(""),
    workLeaderName: formElement(""),
    workLeaderIsActive: formElement(false),
    workLeaderPhoneNumber: formElement(""),
    persons: [{ index: 0, isActive: false, name: "", phoneNumber: "" }],
    projectLeaderName: formElement(""),
    projectLeaderPhoneNumber: formElement(""),
    projectLeaderIsActive: formElement(false),
    isWorkFlammable: formElement(false),
    methodOfWork: formElement([]),
    otherMethodOfWork: formElement(""),
    isAuthorizedForHotWork: formElement(false),
    surveillanceByFireWatch: formElement(false),
    fireWatchName: formElement(""),
    fireWatchNotNeeded: formElement(false),
    postWorkSurveillanceArranged: formElement(false),
    postWorkSurveillanceHours: formElement(1),
    postWorkSurveillanceFireWatch: formElement(""),
    forWorkWithFlammableGoods: formElement(false),
    workplaceCleaned: formElement(false),
    combustibleMaterialsRemoved: formElement(false),
    inAreaWithExplosiveAtmosphere: formElement(false),
    openings: formElement(false),
    extinguishingEquipment: formElement(false),
    weldingEquipment: formElement(false),
    weldingEquipmentFaultless: formElement(false),
    emergencyServicesCanBeCalled: formElement(false),
    waterproofing: formElement(false),
    openFlamesForMeltingIce: formElement(false),
    maximumHeat: formElement(false),
    whenMeltingAsphalt: formElement(false),
    followingSections: formElement(""),
    permitAgreementStartDate: formElement(new Date()),
    permitAgreementEndDate: formElement(new Date()),
    permitAgreementFireWatch: formElement(""),
    permitAgreementWayOfCommunication: formElement(""),
    permitAgreementProviderSignature: Signature_empty(SignatureType.PermitAgreementPermitProvider),
    permitAgreementSeekerSignature: Signature_empty(SignatureType.PermitAgreementPermitSeeker),
    isFinishedPermitProviderSignature: Signature_empty(SignatureType.IsFinishedPermitProvider),
    isFinishedPermitSeekerSignature: Signature_empty(SignatureType.IsFinishedPermitSeeker),
    measures: MeasuresFormState_default(),
    protectiveEquipment: ProtectiveEquipmentFormState_default(),
    riskIdentifications: RiskIdentificationsFormState_default()
});

export function populateErrorMessages(
    formState: GeneralDepartmentPermitFormState,
    problemDetails: ProblemDetails
): GeneralDepartmentPermitFormState {
    const errorKeys = Object.keys(problemDetails.errors);
    const positionsErrorKeys = errorKeys.filter((e) => e.startsWith("Positions"));
    const personsErrorKeys = errorKeys.filter((e) => e.startsWith("Persons"));
    const measuresErrorKeys = errorKeys.filter((e) => e.startsWith("Measures"));
    const formStateKeys = Object.keys(formState);

    const newFormState: GeneralDepartmentPermitFormState = { ...formState };

    formStateKeys.forEach((k) => {
        const capitalizedKey = k[0].toUpperCase() + k.substring(1);

        if (!("errorMessage" in newFormState[k as keyof GeneralDepartmentPermitFormState])) {
            return;
        }

        const element = newFormState[k as keyof GeneralDepartmentPermitFormState] as { errorMessage: string };
        element.errorMessage = "";

        if (!errorKeys.includes(capitalizedKey) || problemDetails.errors[capitalizedKey].length === 0) {
            return;
        }

        const errorMessage = problemDetails.errors[capitalizedKey][0];
        element.errorMessage = errorMessage;
    });

    positionsErrorKeys.forEach((k) => {
        const index = String_extractIndex(k);

        if (index === null) {
            return;
        }

        const positionElement = newFormState.positions[index];

        const isLatitudeError = k.endsWith("Latitude");
        const isLongitudeError = k.endsWith("Longitude");
        const isCommentError = k.endsWith("Comment");

        if (isLatitudeError) {
            positionElement.latitudeErrorMessage = problemDetails.errors[k][0];
        } else if (isLongitudeError) {
            positionElement.longitudeErrorMessage = problemDetails.errors[k][0];
        } else if (isCommentError) {
            positionElement.commentErrorMessage = problemDetails.errors[k][0];
        }
    });

    personsErrorKeys.forEach((k) => {
        const index = String_extractIndex(k);

        if (index === null) {
            return;
        }

        const personElement = newFormState.persons[index];

        const isNameError = k.endsWith("Name");
        const isPhoneNumberError = k.endsWith("PhoneNumber");

        if (isNameError) {
            personElement.nameErrorMessage = problemDetails.errors[k][0];
        } else if (isPhoneNumberError) {
            personElement.phoneNumberErrorMessage = problemDetails.errors[k][0];
        }
    });

    measuresErrorKeys.forEach((k) => {
        let measuresKey = Array_lastOrDefault(k.split(".")) as keyof MeasuresFormState;
        if (!measuresKey) {
            console.error(`Could not get measuresKey from: ${k}`);
            return;
        }

        measuresKey = String_deCapitalize(measuresKey) as keyof MeasuresFormState;

        if (!("errorMessage" in newFormState.measures[measuresKey])) {
            return;
        }

        newFormState.measures[measuresKey] = {
            ...newFormState.measures[measuresKey],
            errorMessage: problemDetails.errors[k][0]
        } as any;
    });

    return newFormState;
}

export function toFormState(permit: GeneralDepartmentPermit): GeneralDepartmentPermitFormState {
    const populatedFormState: GeneralDepartmentPermitFormState = {
        positions: permit.positions.map(Position_toPositionPickerRow),
        startDate: formElement(permit.startDate),
        endDate: formElement(permit.endDate),
        remindMe: formElement(permit.remindMe),
        expirationReminderEmail: formElement(permit.expirationReminderEmail),
        expirationReminderPhoneNumber: formElement(permit.expirationReminderPhoneNumber),
        remindMeDate: formElement(permit.remindMeDate),
        permitCategory: formElement(permit.permitCategory),
        entrepreneur: formElement(permit.entrepreneur),
        description: formElement(permit.description),
        workLeaderName: formElement(permit.workLeaderName),
        workLeaderIsActive: formElement(permit.workLeaderIsActive),
        workLeaderPhoneNumber: formElement(permit.workLeaderPhoneNumber),
        persons: permit.persons.map((p, i) => ({
            index: i,
            isActive: p.isActive,
            name: p.name,
            phoneNumber: p.phoneNumber
        })),
        projectLeaderName: formElement(permit.projectLeaderName),
        projectLeaderPhoneNumber: formElement(permit.projectLeaderPhoneNumber),
        projectLeaderIsActive: formElement(permit.projectLeaderIsActive),
        isWorkFlammable: formElement(permit.isWorkFlammable),
        methodOfWork: formElement(permit.methodOfWork),
        otherMethodOfWork: formElement(permit.otherMethodOfWork),
        isAuthorizedForHotWork: formElement(permit.isAuthorizedForHotWork),
        surveillanceByFireWatch: formElement(permit.surveillanceByFireWatch),
        fireWatchName: formElement(permit.fireWatchName),
        fireWatchNotNeeded: formElement(permit.fireWatchNotNeeded),
        postWorkSurveillanceArranged: formElement(permit.postWorkSurveillanceArranged),
        postWorkSurveillanceHours: formElement(permit.postWorkSurveillanceHours),
        postWorkSurveillanceFireWatch: formElement(permit.postWorkSurveillanceFireWatch),
        forWorkWithFlammableGoods: formElement(permit.forWorkWithFlammableGoods),
        workplaceCleaned: formElement(permit.workplaceCleaned),
        combustibleMaterialsRemoved: formElement(permit.combustibleMaterialsRemoved),
        inAreaWithExplosiveAtmosphere: formElement(permit.inAreaWithExplosiveAtmosphere),
        openings: formElement(permit.openings),
        extinguishingEquipment: formElement(permit.extinguishingEquipment),
        weldingEquipment: formElement(permit.weldingEquipment),
        weldingEquipmentFaultless: formElement(permit.weldingEquipmentFaultless),
        emergencyServicesCanBeCalled: formElement(permit.emergencyServicesCanBeCalled),
        waterproofing: formElement(permit.waterproofing),
        openFlamesForMeltingIce: formElement(permit.openFlamesForMeltingIce),
        maximumHeat: formElement(permit.maximumHeat),
        whenMeltingAsphalt: formElement(permit.whenMeltingAsphalt),
        followingSections: formElement(permit.followingSections),
        permitAgreementStartDate: formElement(permit.permitAgreementStartDate),
        permitAgreementEndDate: formElement(permit.permitAgreementEndDate),
        permitAgreementFireWatch: formElement(permit.permitAgreementFireWatch),
        permitAgreementWayOfCommunication: formElement(permit.permitAgreementWayOfCommunication),
        permitAgreementProviderSignature: {
            ...permit.permitAgreementProviderSignature,
            signatureType: SignatureType.PermitAgreementPermitProvider
        },
        permitAgreementSeekerSignature: {
            ...permit.permitAgreementSeekerSignature,
            signatureType: SignatureType.PermitAgreementPermitSeeker
        },
        isFinishedPermitProviderSignature: {
            ...permit.isFinishedPermitProviderSignature,
            signatureType: SignatureType.IsFinishedPermitProvider
        },
        isFinishedPermitSeekerSignature: {
            ...permit.isFinishedPermitSeekerSignature,
            signatureType: SignatureType.IsFinishedPermitSeeker
        },
        measures: Measures_toFormState(permit.measures),
        protectiveEquipment: ProtectiveEquipment_toFormState(permit.protectiveEquipment),
        riskIdentifications: RiskIdentifications_toFormState(permit.riskIdentifications)
    };

    return populatedFormState;
}

export function clearErrors(formState: GeneralDepartmentPermitFormState): GeneralDepartmentPermitFormState {
    const formStateKeys = Object.keys(formState);

    const newFormState: GeneralDepartmentPermitFormState = { ...formState };

    formStateKeys.forEach((k) => {
        if (!("errorMessage" in newFormState[k as keyof GeneralDepartmentPermitFormState])) {
            return;
        }

        const element = newFormState[k as keyof GeneralDepartmentPermitFormState] as { errorMessage: string };
        element.errorMessage = "";
    });

    newFormState.positions.forEach((p) => {
        p.latitudeErrorMessage = "";
        p.longitudeErrorMessage = "";
    });

    newFormState.persons.forEach((p) => {
        p.nameErrorMessage = "";
        p.phoneNumberErrorMessage = "";
    });

    return newFormState;
}
